/* global google */
"use client";

import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

import { TStoresFormatted } from "./mapLoaderUtils/mapLoaderUtils";
import { TCoordinates } from "../StoreFinder/StoreFinder";

type TClosestStores = {
  lat: number;
  lng: number;
  name?: string;
  distance?: number;
  pin: string;
};

type TMapLocation = {
  location?: TCoordinates;
  stores: TStoresFormatted[];
  closestStores?: TClosestStores[];
  mapKey: string;
};

const defaultLocation = {
  lat: 51.515605,
  lng: -0.145165,
  title: "John Lewis Oxford Street",
};

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  minHeight: "200px",
  border: "3px solid #D2CABC",
  borderRadius: "8px",
};

const GOOGLE_MAP_LIBRARIES: ("places" | "geometry")[] = [];

export const getClusterColor = (count: number) => {
  if (count > 20) return "#CC1526";
  return "#102b2b";
};

const MapLoader = ({
  location,
  stores,
  closestStores,
  mapKey,
}: TMapLocation) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  const mapCenter = location?.lat && location?.lng ? location : defaultLocation;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: mapKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  const customRenderer = {
    render({
      count,
      position,
    }: {
      count: number;
      position: google.maps.LatLng;
    }) {
      const color = getClusterColor(count);

      const svg = window.btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <circle cx="20" cy="20" r="20" fill="${color}" />
          <text x="20" y="25" text-anchor="middle" font-size="14" fill="white" font-family="Arial">${count}</text>
        </svg>
      `);

      return new google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;base64,${svg}`,
          scaledSize: new google.maps.Size(35, 35),
        },
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
      });
    },
  };

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    const storesOnMap =
      closestStores && closestStores.length > 0 ? closestStores : stores;

    const newMarkers = storesOnMap.map((store) => {
      return new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        title: store.name,
        icon: {
          url: store.pin,
        },
      });
    });

    markersRef.current = newMarkers;

    clustererRef.current = new MarkerClusterer({
      markers: newMarkers,
      map: mapRef.current,
      renderer: customRenderer,
    });

    if (closestStores && closestStores.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      closestStores.forEach(({ lat, lng }) => {
        bounds.extend({ lat, lng });
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [mapLoaded, stores, closestStores]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      zoom={17}
      onLoad={handleMapLoad}
      center={mapCenter}
      options={{ mapId: "store-finder-id" }}
    />
  );
};

export default MapLoader;
